import { useState } from "react";
import { create, show, useModal } from "@ebay/nice-modal-react";
import { useMutation } from "@apollo/client/react";

import ConfirmationModal from "ds/components/ConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import Counter from "ds/components/Counter";
import Typography from "ds/components/Typography";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";

import { ChangeHistoryEntry, InputSnapshot } from "../types";
import InputChangeItem from "./components/InputChangeItem";
import { ROLLBACK_DEPLOYMENT, CHANGE_HISTORY_QUERY } from "../gql";
import { TEMPLATE_DEPLOYMENT_QUERY } from "../../gql";
import { diffInputs } from "../helpers";
import styles from "./styles.module.css";

type RollbackConfirmationModalProps = {
  entry: ChangeHistoryEntry;
  templateId: string;
  deploymentId: string;
  currentInputs: InputSnapshot[];
  currentVersionNumber: string;
  onSuccess?: () => void;
};

const RollbackConfirmationModal = create(function RollbackConfirmationModal({
  entry,
  templateId,
  deploymentId,
  currentInputs,
  currentVersionNumber,
  onSuccess,
}: RollbackConfirmationModalProps) {
  const modal = useModal();
  const { reportSuccess, reportError } = useTypedContext(FlashContext);
  const [loading, setLoading] = useState(false);

  const [rollbackDeployment] = useMutation(ROLLBACK_DEPLOYMENT, {
    refetchQueries: [CHANGE_HISTORY_QUERY, TEMPLATE_DEPLOYMENT_QUERY],
  });

  const targetVersionNumber = entry.targetVersionNumber;
  const hasVersionChange = currentVersionNumber !== targetVersionNumber;
  const uniqueInputChanges = diffInputs(entry.inputs, currentInputs);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await rollbackDeployment({
        variables: {
          templateID: templateId,
          id: deploymentId,
          changeRecordID: entry.id,
        },
      });
      reportSuccess({ message: "Rollback successful" });
      modal.hide();
      modal.resolve();
      onSuccess?.();
    } catch (error) {
      reportError({ message: `Rollback failed: ${(error as Error).message}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfirmationModal
      title="Confirm revert"
      confirmationButtonLabel="Revert"
      confirmationButtonVariant="dangerPrimary"
      onConfirm={handleConfirm}
      isLoading={loading}
      isDismissable={!loading}
      size="large"
    >
      <div className={styles.summary}>
        <ConfirmationModalMessage>
          This action will revert the deployment to its previous state. The following changes will
          be applied:
        </ConfirmationModalMessage>

        {hasVersionChange && (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Typography tag="p" variant="p-t7">
                Version rollback
              </Typography>
            </div>
            <Box gap="small" align="center">
              <Typography tag="span" variant="p-body3" color="secondary">
                Template version will revert from
              </Typography>
              <Badge variant="yellow" type="weak" text={currentVersionNumber} />
              <Typography tag="span" variant="p-body3" color="secondary">
                back to
              </Typography>
              <Badge variant="yellow" type="weak" text={targetVersionNumber} />
            </Box>
          </div>
        )}

        {uniqueInputChanges.length > 0 && (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Typography tag="p" variant="p-t7">
                Input changes
              </Typography>
              <Counter count={uniqueInputChanges.length} size="small" />
            </div>
            <div className={styles.inputList}>
              {uniqueInputChanges.map((change) => (
                <InputChangeItem key={change.fieldId} change={change} />
              ))}
            </div>
          </div>
        )}
      </div>
    </ConfirmationModal>
  );
});

export const showRollbackConfirmationModal = (props: RollbackConfirmationModalProps) =>
  show(RollbackConfirmationModal, props);
